'use client';

import { Suspense } from 'react';

import { useTheme } from 'next-themes';
import dynamic from 'next/dynamic';

import type { ToasterProps } from 'sonner';

// Lazy-load `sonner`'s renderer: the toast host only matters once a `toast(...)`
// fires — never on first paint — so its bundle stays out of a route's first-load
// and code-splits into its own chunk that loads when the host actually mounts.
// `ssr: false` keeps it off the server render (nothing to paint until a
// client-side toast is queued). The imperative `toast` re-export below is
// unaffected — callers that fire toasts already run on interaction.
const Sonner = dynamic(() => import('sonner').then(mod => mod.Toaster), {
  ssr: false,
});

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();

  // The `ssr: false` `Sonner` MUST sit under a `<Suspense>` boundary: without
  // one, rendering it during a server prerender (Next 16 `cacheComponents`)
  // throws "Bail out to client-side rendering: next/dynamic" and takes the whole
  // subtree to CSR. Owning the boundary here keeps every `<Toaster />` consumer
  // self-contained — no call site needs to remember to wrap it.
  return (
    <Suspense fallback={null}>
      <Sonner
        theme={theme as ToasterProps['theme']}
        className="toaster group"
        toastOptions={{
          classNames: {
            toast:
              'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg',
            description: 'group-[.toast]:text-muted-foreground',
            actionButton:
              'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
            cancelButton:
              'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
            // Error variant styling lives in `apps/web/app/globals.css`
            // — Tailwind classNames here can't out-specify Sonner's own
            // CSS-variable styling, so we target `[data-sonner-toast]
            // [data-type='error']` globally instead.
          },
        }}
        {...props}
      />
    </Suspense>
  );
};

export { toast } from 'sonner';
export { Toaster };
